import { forwardRef, useEffect, useImperativeHandle } from 'react';
import * as React from 'react';
import './style.scss';
import type IValidatable from '../validatable.interface';
import ValidationInfo from '../../../validation/validation-info';
import ValidationEngine from '../../../validation/validation-engine';
import type { IServerValidationError } from '../validatable.interface';
import RequiredAsterisk from '../RequiredAsterisk';

interface IPercentSliderFormGroupState {
  value: number;
  validationInfo: ValidationInfo;
}

interface IPercentSliderFormGroupProps {
  id: string;
  className?: string;
  serverValidationName?: string;
  labelText: string;
  ariaDescribedby?: string;
  showPercentage?: boolean;
  value: number;
  state: IPercentSliderFormGroupState | [IPercentSliderFormGroupState, React.Dispatch<React.SetStateAction<IPercentSliderFormGroupState>>];
  onChange?: (value: number | undefined) => void;
}

export default forwardRef<IValidatable, IPercentSliderFormGroupProps>(function PercentSliderFormGroup(
  { id, className, serverValidationName, labelText, ariaDescribedby, showPercentage = true, value, state, onChange },
  ref
) {
  const stateDerived = Array.isArray(state) ? state[0] : state;

  const rangeSliderRef = React.useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${labelText} - `);
      ValidationEngine.for(updatedValidationInfo).requiredIf(true, stateDerived.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${labelText} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), [])
      );
    },
  }));

  useEffect(() => {
    if (rangeSliderRef.current) {
      rangeSliderRef.current.style.background = `linear-gradient(to right, #0079b4 ${value}%, #dfe0e3 ${value}%)`;
    }
  }, [value]);

  return (
    <div className={`percentage-slider ${className ?? ''}`}>
      <label htmlFor={`${id}-slider`} className="form-label">
        {labelText}
        <RequiredAsterisk />
      </label>
      <div id={id} className={`d-flex align-items-center ${stateDerived.validationInfo.isInvalid ? 'is-invalid' : ''}`}>
        <input
          id={`${id}-slider`}
          type="range"
          className="form-range flex-grow-1"
          min="0"
          max="100"
          aria-invalid={stateDerived.validationInfo.isInvalid ? 'true' : 'false'}
          value={value}
          onChange={(e) => (Array.isArray(state) ? state[1]({ ...state[0], value: Number(e.target.value) }) : onChange?.(e.target.value !== '' ? Number(e.target.value) : undefined))}
          ref={rangeSliderRef}
        />
        {showPercentage && (
          <div className="input-group flex-shrink-1 ms-2">
            <label className="visually-hidden" htmlFor={`${id}-number`}>
              {labelText}
            </label>
            <input
              id={`${id}-number`}
              type="number"
              className={`form-control percentage-input ${stateDerived.validationInfo.isInvalid ? 'border-danger' : ''}`}
              aria-describedby={ariaDescribedby}
              value={stateDerived.value !== undefined ? stateDerived.value : ''}
              inputMode="numeric"
              min="0"
              max="100"
              aria-invalid={stateDerived.validationInfo.isInvalid ? 'true' : 'false'}
              onChange={(e) => (Array.isArray(state) ? state[1]({ ...state[0], value: Number(e.target.value) }) : onChange?.(e.target.value !== '' ? Number(e.target.value) : undefined))}
            />
            <span className={`input-group-text ${stateDerived.validationInfo.isInvalid ? 'border-danger' : ''}`}>%</span>
          </div>
        )}
      </div>
      {stateDerived.validationInfo.isInvalid && (
        <div id={`${id}-error`} className="invalid-feedback">
          {stateDerived.validationInfo.getErrors().map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
    </div>
  );
});
